import React, { useState, useEffect } from 'react';
import { Home, Plus, Edit2, Trash2, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface VoucherHead {
  id: string;
  head_name: string;
  head_code: string;
  head_type: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

export default function VoucherHead() {
  const [voucherHeads, setVoucherHeads] = useState<VoucherHead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingHead, setEditingHead] = useState<VoucherHead | null>(null);
  const [formData, setFormData] = useState({
    head_name: '',
    head_code: '',
    head_type: 'expense',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    loadVoucherHeads();
  }, []);

  const loadVoucherHeads = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setVoucherHeads(getMockVoucherHeads());
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('voucher_heads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVoucherHeads(data || []);
    } catch (error) {
      console.error('Error loading voucher heads:', error);
      setVoucherHeads(getMockVoucherHeads());
    } finally {
      setLoading(false);
    }
  };

  const getMockVoucherHeads = (): VoucherHead[] => {
    return [
      {
        id: '1',
        head_name: 'Medical Supplies',
        head_code: 'VH-001',
        head_type: 'expense',
        description: 'Medical supplies and equipment',
        is_active: true,
        created_at: '2025-01-27',
      },
      {
        id: '2',
        head_name: 'Staff Salary',
        head_code: 'VH-002',
        head_type: 'expense',
        description: 'Staff salary payments',
        is_active: true,
        created_at: '2025-01-26',
      },
      {
        id: '3',
        head_name: 'Utility Bills',
        head_code: 'VH-003',
        head_type: 'expense',
        description: 'Electricity, water, gas bills',
        is_active: true,
        created_at: '2025-01-25',
      },
    ];
  };

  const filteredHeads = voucherHeads.filter(head => {
    const matchesSearch = 
      head.head_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      head.head_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      head.head_type.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        alert('Voucher head saved successfully (mock mode)');
        setShowForm(false);
        resetForm();
        return;
      }

      if (editingHead) {
        const { error } = await supabase
          .from('voucher_heads')
          .update(formData)
          .eq('id', editingHead.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('voucher_heads')
          .insert([formData]);
        
        if (error) throw error;
      }

      await loadVoucherHeads();
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Error saving voucher head:', error);
      alert('Error saving voucher head');
    }
  };

  const handleEdit = (head: VoucherHead) => {
    setEditingHead(head);
    setFormData({
      head_name: head.head_name,
      head_code: head.head_code,
      head_type: head.head_type,
      description: head.description || '',
      is_active: head.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this voucher head?')) return;

    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        alert('Voucher head deleted successfully (mock mode)');
        return;
      }

      const { error } = await supabase
        .from('voucher_heads')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadVoucherHeads();
    } catch (error) {
      console.error('Error deleting voucher head:', error);
      alert('Error deleting voucher head');
    }
  };

  const resetForm = () => {
    setFormData({
      head_name: '',
      head_code: '',
      head_type: 'expense',
      description: '',
      is_active: true,
    });
    setEditingHead(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Voucher Head</span>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Voucher Head</span>
          </button>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        {showForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingHead ? 'Edit Voucher Head' : 'Add New Voucher Head'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Head Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.head_name}
                    onChange={(e) => setFormData({ ...formData, head_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Head Code *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.head_code}
                    onChange={(e) => setFormData({ ...formData, head_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Head Type *
                  </label>
                  <select
                    required
                    value={formData.head_type}
                    onChange={(e) => setFormData({ ...formData, head_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="expense">Expense</option>
                    <option value="income">Income</option>
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  {editingHead ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search voucher heads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Head Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Head Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredHeads.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No voucher heads found
                    </td>
                  </tr>
                ) : (
                  filteredHeads.map((head) => (
                    <tr key={head.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {head.head_code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {head.head_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          head.head_type === 'expense' ? 'bg-red-100 text-red-800' :
                          head.head_type === 'income' ? 'bg-green-100 text-green-800' :
                          head.head_type === 'asset' ? 'bg-blue-100 text-blue-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          {head.head_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {head.description || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          head.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {head.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(head)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(head.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
